/**
 * Onboarding Form Step (V2)
 *
 * Used when the OTP-verified email doesn't match an existing patient (or the
 * DOB challenge fell through). Wraps the SAME PatientSelfServiceForm the
 * clinic-sent /patient-form/:token flow uses, so new-patient UX is identical
 * across both entry points.
 */

import React from 'react';
import { z } from 'zod';
import PatientSelfServiceForm from '@/components/patient/PatientSelfServiceForm';
import { patientFormPublicSchema } from '@/validators/patientFormPublic.schema';

type PatientFormData = z.infer<typeof patientFormPublicSchema>;

interface OnboardingFormStepProps {
  clinicName: string;
  verifiedEmail: string;
  onSubmit: (data: PatientFormData) => Promise<void>;
  isSubmitting: boolean;
}

export const OnboardingFormStep: React.FC<OnboardingFormStepProps> = ({
  clinicName,
  verifiedEmail,
  onSubmit,
  isSubmitting,
}) => {
  // Pre-fill the verified email so the patient never has to re-type it
  // and so it's marked as the default email contact on the resulting record.
  const prefilled = {
    contacts: [{ key: 'email', value: verifiedEmail, is_default: true }],
  };

  return (
    <div>
      <h2 className='mb-2 text-2xl font-bold text-slate-900'>Tell us about yourself</h2>
      <p className='mb-6 text-slate-500'>
        We don&apos;t have a record for this email yet. Please complete the patient form below
        — your appointment will be booked as soon as you submit.
      </p>

      <PatientSelfServiceForm
        clinicName={clinicName}
        patientData={prefilled}
        onSubmit={onSubmit}
        isLoading={isSubmitting}
      />
    </div>
  );
};
